import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { chosenFocalPoint, visibleItems } from "@/components/cms/SectionRenderer";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useMediaByKey } from "@/content-context";
import { volunteering } from "@/data/media";
import { buildMetadata } from "@/lib/seo";
import { imageReveal } from "@/lib/motion";
import { pageLoader } from "@/page-data";

/**
 * Volunteer with us.
 *
 * The primary action opens the existing contact form with "Volunteering"
 * already selected, and keeps the visitor on this site. There is deliberately
 * no application process, no eligibility rule, no duration and no price:
 * HISCUS has published none of those, and putting them on a page would commit
 * the organisation to terms it never set.
 */

export const loader = pageLoader("volunteer");

export const meta = () =>
  buildMetadata({
    title: "Volunteer with HISCUS",
    description:
      "Volunteer on live HISCUS programme work in Nepal: session preparation, reusable pad making, supplies and distribution, documentation, and school and infrastructure days.",
    path: "/get-involved/volunteer",
  });

function IntroSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  return (
    <Section>
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal variants={imageReveal}>
          <MediaFigure
            item={byKey(section.mediaKey) ?? volunteering[0]}
            ratio="4 / 3"
            sizes="(min-width: 1024px) 50vw, 100vw"
            objectPosition={chosenFocalPoint(section.settings?.focalPoint)}
          />
        </Reveal>
        <Reveal>
          {section.title ? <h2 style={{ fontSize: "var(--step-h2)" }}>{section.title}</h2> : null}
          <Blocks blocks={section.body} className="mt-5" />
        </Reveal>
      </div>
    </Section>
  );
}

function RolesSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const roles = visibleItems(section);
  if (!roles.length) return null;
  return (
    <Section tone="ivory" ariaLabelledBy="roles-heading">
      <SectionHeading id="roles-heading" eyebrow={section.eyebrow} title={section.title || "What volunteers do"} lead={section.lead} />
      <RevealGroup as="ul" className="mt-10 grid gap-6 sm:grid-cols-2">
        {roles.map((role) => {
          const image = byKey(role.mediaKey);
          return (
            <Reveal as="li" key={role.title}>
              <div className="h-full overflow-hidden rounded-[16px] border border-line bg-white">
                {image ? (
                  <MediaFigure
                    item={image}
                    ratio="16 / 10"
                    sizes="(min-width: 640px) 45vw, 100vw"
                    showCaption={false}
                    radius={0}
                    objectPosition={role.focalPoint || undefined}
                  />
                ) : null}
                <div className="p-6">
                  <h3 style={{ fontSize: "var(--step-h4)" }}>{role.title}</h3>
                  {role.text ? <p className="mt-3 text-muted">{role.text}</p> : null}
                </div>
              </div>
            </Reveal>
          );
        })}
      </RevealGroup>
    </Section>
  );
}

function PracticalitiesSection({ section }: SectionProps) {
  return (
    <Section id="volunteer-enquiry" ariaLabelledBy="practical-heading">
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-16">
        <Reveal>
          <SectionHeading
            id="practical-heading"
            eyebrow={section.eyebrow}
            title={section.title || "The questions worth asking first"}
          />
          <dl className="mt-8 space-y-7">
            {visibleItems(section).map((entry) => (
              <div key={entry.title}>
                <dt className="font-serif text-[1.1875rem] text-midnight">{entry.title}</dt>
                <dd className="mt-2 text-muted">{entry.text}</dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <Reveal>
          <EnquiryForm
            heading={section.texts?.formHeading || "Enquire about volunteering"}
            defaultInterest="volunteer"
            formKey="volunteer"
          />
        </Reveal>
      </div>
    </Section>
  );
}

export const volunteerSections: SectionRenderers = {
  "volunteer.intro": IntroSection,
  "volunteer.roles": RolesSection,
  "volunteer.practicalities": PracticalitiesSection,
};

export function VolunteerPage() {
  return (
    <>
      <PageHeader
        eyebrow="Volunteer with us"
        title="Do the work the programmes are actually made of"
        lead="Volunteers with HISCUS cut fabric, carry supplies, prepare sessions and write them up. It is ordinary, useful work, and that is the point."
        trail={[
          { name: "Home", href: "/" },
          { name: "Get Involved", href: "/get-involved" },
          { name: "Volunteer with us", href: "/get-involved/volunteer" },
        ]}
        actions={
          <div className="flex flex-col gap-3 sm:flex-row">
            <ButtonLink href="/contact?interest=volunteer">
              Volunteer with us
              <ArrowRight />
            </ButtonLink>
            <ButtonLink href="#volunteer-enquiry" variant="secondary">
              Ask a question first
            </ButtonLink>
          </div>
        }
      />
      <PageSections renderers={volunteerSections} />
    </>
  );
}
